import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import styles from "./PayOnlineModal.module.css";
import qrPlaceholder from "../../assets/images/qr-placeholder.svg";
import CustomTextInput from "../CustomTextInput/CustomTextInput";
import CustomFileInput from "../CustomFiles/CustomFiles";
import AxiosInstance, { TokenService } from "../../services/Api";

const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return `${num < 0 ? "-" : ""}₹${Math.abs(num).toLocaleString("en-IN")}`;
};

const PayOnlineModal = ({
  open,
  onClose,
  onCancel,
  onSubmit,
  title = "Pay Online",
  student = {},
  paymentMode = "Online",
  totalAmount = 0,
  submitting = false,
  schoolId, // optional — falls back to the logged-in user's school
}) => {
  const [upiTransactionId, setUpiTransactionId] = useState("");
  const [amountPayingNow, setAmountPayingNow] = useState("");
  const [paymentImage, setPaymentImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [bankQr, setBankQr] = useState(null); // {bank_name, qr_code_image}
  const [qrImageSrc, setQrImageSrc] = useState(""); // QR image fetched as blob

  useEffect(() => {
    if (open) {
      setUpiTransactionId("");
      setAmountPayingNow("");
      setPaymentImage(null);
      setErrors({});
    }
  }, [open]);

  // Load the school's configured Bank QR when the modal opens.
  // The image itself is streamed through the authenticated API and shown
  // as a blob object URL (same logic as study material downloads).
  useEffect(() => {
    if (!open) return undefined;

    const resolvedSchoolId =
      schoolId ||
      TokenService.getUser()?.client_data?.[0]?.school_id ||
      "";

    if (!resolvedSchoolId) {
      setBankQr(null);
      setQrImageSrc("");
      return undefined;
    }

    let cancelled = false;
    let objectUrl = "";

    AxiosInstance.get(`/settings/qr-code?school_id=${resolvedSchoolId}`)
      .then((response) => {
        if (cancelled) return null;
        const config = response.data?.data || null;
        setBankQr(config);

        if (!config?.qr_code_image) {
          setQrImageSrc("");
          return null;
        }

        return AxiosInstance.get(
          `/settings/qr-code/image?school_id=${resolvedSchoolId}`,
          { responseType: "blob" },
        );
      })
      .then((imageResponse) => {
        if (cancelled || !imageResponse) return;
        objectUrl = URL.createObjectURL(new Blob([imageResponse.data]));
        setQrImageSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setBankQr(null);
          setQrImageSrc("");
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, schoolId]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const amountNum = Number(amountPayingNow) || 0;

  /* ── FIX 1 ────────────────────────────────────────────
     "Balance after payment" is the outstanding amount and
     must stay STATIC while the user types.
     Was: Math.max(totalAmount - amountNum, 0)  <- moved on
     every keystroke because it depended on amountNum.     */
  const balanceAfterPayment = Number(totalAmount) || 0;

  /* ── FIX 2 ────────────────────────────────────────────
     The typed amount may not exceed that balance.         */
  const validateAmount = (value) => {
    const raw = String(value).trim();
    if (!raw) return "Enter a valid amount";
    const num = Number(raw);
    if (Number.isNaN(num) || num <= 0) return "Enter a valid amount";
    if (num > balanceAfterPayment) {
      return `Amount cannot exceed the balance of ${formatCurrency(
        balanceAfterPayment,
      )}`;
    }
    return "";
  };

  const handleAmountChange = (e) => {
    const cleaned = e.target.value.replace(/[^\d.]/g, "");
    setAmountPayingNow(cleaned);
    // live-validate so the message clears as soon as the value is valid
    setErrors((prev) => ({
      ...prev,
      amountPayingNow: validateAmount(cleaned),
    }));
  };

  const MAX_IMAGE_SIZE_BYTES = 25 * 1024 * 1024;

  const handleFileChange = (file) => {
    const selected = file?.target?.files?.[0] ?? file;

    if (selected && selected.size > MAX_IMAGE_SIZE_BYTES) {
      setPaymentImage(null);
      setErrors((prev) => ({
        ...prev,
        paymentImage: "Image size must not exceed 25MB",
      }));
      return;
    }

    setPaymentImage(selected);
    setErrors((prev) => ({ ...prev, paymentImage: "" }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      (onCancel || onClose)?.();
    }
  };

  const handleSubmit = () => {
    const nextErrors = {};
    if (!upiTransactionId.trim()) {
      nextErrors.upiTransactionId = "Enter the UPI Transaction ID";
    }
    const amountError = validateAmount(amountPayingNow);
    if (amountError) {
      nextErrors.amountPayingNow = amountError;
    }
    if (!paymentImage) {
      nextErrors.paymentImage = "Upload the payment screenshot";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    onSubmit?.({
      upiTransactionId: upiTransactionId.trim(),
      amountPayingNow: amountNum,
      paymentImage,
    });
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.qrColumn}>
            <div className={styles.qrBox}>
              <img
                src={qrImageSrc || qrPlaceholder}
                alt="Payment QR code"
                onError={(e) => {
                  e.currentTarget.src = qrPlaceholder;
                }}
              />
            </div>
            {bankQr?.bank_name ? (
              <div className={styles.qrBankName}>{bankQr.bank_name}</div>
            ) : null}
            {/* <div className={styles.qrAmount}>{formatCurrency(totalAmount)}</div> */}
          </div>

          <div className={styles.formColumn}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Student</span>
              <span className={styles.summaryValue}>{student.name}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Admission ID</span>
              <span className={styles.summaryValue}>{student.admissionId}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Class</span>
              <span className={styles.summaryValue}>
                {student.classSection}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Payment mode</span>
              <span className={styles.summaryValue}>{paymentMode}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Balance after payment</span>
              <span className={styles.summaryValue}>
                {formatCurrency(balanceAfterPayment)}
              </span>
            </div>

            <div className={styles.divider} />

            <CustomTextInput
              label="UPI Transaction ID"
              name="upi_transaction_id"
              placeholder="Enter UPI Transaction ID"
              value={upiTransactionId}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                setUpiTransactionId(sanitized);
                if (errors.upiTransactionId) {
                  setErrors((prev) => ({ ...prev, upiTransactionId: "" }));
                }
              }}
              required
              error={errors.upiTransactionId}
              labelClassName={styles.fieldLabel}
              inputClassName={styles.fieldInput}
            />

            <CustomTextInput
              label="Amount paying now"
              name="amount_paying_now"
              placeholder="Enter Amount paying now"
              value={amountPayingNow}
              onChange={handleAmountChange}
              required
              error={errors.amountPayingNow}
              labelClassName={styles.fieldLabel}
              inputClassName={styles.fieldInput}
            />

            <CustomFileInput
              label="Upload Payment Image (PNG, JPEG)"
              name="payment_image"
              value={paymentImage}
              onChange={handleFileChange}
              accept="image/png,image/jpeg"
              styleFileName={styles.styleFileName}
              required
              error={errors.paymentImage}
              labelClassName={styles.fieldLabel}
            />
          </div>
        </div>

        <p className={styles.disclaimer}>
          Receipt will be generated after it is recorded in the fee ledger.
          Submit your payment screenshot in the payment attachment field.
        </p>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel || onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={submitting || Boolean(errors.amountPayingNow)}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayOnlineModal;
